import React, { useState, useEffect } from 'react'
import SearchBar from './search-bar'
import { makeStyles } from '@material-ui/styles'
import { LinearProgress, TableContainer } from '@material-ui/core'
import withSearch from './with-search'
import DisclaimerWithTwoCta from 'components/disclaimer-with-two-cta'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Button from '@material-ui/core/Button'
import NewLeads from './new-leads'
import ContactedLeads from './contacted-leads'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import UpgradeSuccessModal from 'components/upgrade-success-modal'

export const useStyles = makeStyles(theme => ({
  leadNameCell: {
    paddingLeft: '1.5rem',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  containerStyles: {
    boxSizing: 'border-box',
    padding: '0 36px 1rem',
    maxWidth: '100%',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
      marginBottom: '60px',
      padding: '0 8px 1rem',
    },
  },
}))

const LeadsTable = ({
  filteredItems,
  coachingLeadContactMethodOptions = [],
  setUnviewedLeadCount,
  refetch,
  freeTierLimitsReached,
  tab,
}) => {
  const classes = useStyles()
  const [filteredLeads, setFilteredLeads] = useState(filteredItems)
  const [searchTerm, setSearchTerm] = useState('')
  const isLeadsLimitReached = freeTierLimitsReached.includes('LEADS')
  const [showDisclaimer, setShowDisclaimer] = useState(isLeadsLimitReached)

  const {
    upgradeLoading: loading,
    handleUpgradeClick,
    showSuccessModal,
    handleDismiss,
  } = useUpgradeFlow('leads')

  // true if search term is empty - '' - or contained by name; false otherwise
  const nameMatchesSearchToken = ({ firstName, lastName }, token) => {
    const fullName = `${firstName} ${lastName}`.toLowerCase().trim()
    const term = token.toLowerCase().trim()

    return fullName.includes(term)
  }

  // inside the call to searchableItems.filter, return cond && otherCond && ... for filtering purposes
  //
  // In doing this we can compose filters.
  //
  // Make sure to update the effect dependencies list.
  useEffect(() => {
    const filtered = filteredItems.filter(item =>
      nameMatchesSearchToken(item, searchTerm),
    )
    setFilteredLeads(filtered)
  }, [searchTerm, filteredItems])

  const sortByCreatedOn = (a, b, dir) => {
    a = a.props.lead.createdAt
    b = b.props.lead.createdAt

    if (a < b) return dir === 'desc' ? 1 : -1
    if (a === b) return 0
    if (a > b) return dir === 'desc' ? -1 : 1
  }

  const sortByFullName = (a, b, dir) => {
    a = (
      a.props.lead.firstName.toLowerCase() + a.props.lead.lastName.toLowerCase()
    ).replace(/\s/g, '')
    b = (
      b.props.lead.firstName.toLowerCase() + b.props.lead.lastName.toLowerCase()
    ).replace(/\s/g, '')

    if (a > b) return dir === 'desc' ? 1 : -1
    if (a < b) return dir === 'desc' ? -1 : 1
    return 0
  }

  const awaitingResponseLeads = filteredLeads.filter(lead => !lead.contactedAt)
  const contactedLeads = filteredLeads.filter(lead => !!lead.contactedAt)

  return (
    <>
      <TableContainer className={classes.containerStyles}>
        {loading && <LinearProgress color="secondary" />}
        <DisclaimerWithTwoCta
          icon={<InfoOutlinedIcon style={{ color: '#554427' }} />}
          description="Enjoy the benefits of your free account with up to two leads. Want more? Upgrade now to receive unlimited coaching requests."
          leftCta={
            <Button
              variant="text"
              color="primary"
              onClick={handleUpgradeClick}
              style={{ paddingLeft: 0 }}
            >
              Upgrade Now
            </Button>
          }
          rightCta={
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                setShowDisclaimer(false)
              }}
            >
              Got It
            </Button>
          }
          show={showDisclaimer}
        />
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Search Leads"
          data-testid="leads-list-search"
        />
        {tab === 'new' ? (
          <NewLeads
            awaitingResponseLeads={awaitingResponseLeads}
            contactMethodOptions={coachingLeadContactMethodOptions}
            setUnviewedLeadCount={setUnviewedLeadCount}
            refetch={refetch}
            sortByFullName={sortByFullName}
            sortByCreatedOn={sortByCreatedOn}
          />
        ) : (
          <ContactedLeads
            contactedLeads={contactedLeads}
            setUnviewedLeadCount={setUnviewedLeadCount}
            refetch={refetch}
            sortByFullName={sortByFullName}
            sortByCreatedOn={sortByCreatedOn}
          />
        )}
      </TableContainer>

      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )
}

export default withSearch(LeadsTable)
